import type { PlanRead } from './plan';
import type { Box, PathPoint, SeatGeom } from './types';

/*
  Зонд пути и посадки (spec-3-motion §4): капля ни на кадр не проходит над текстом, значками, бейджами,
  кнопками, полями, крестиками вкладок, кольцами фокуса и волоском ручки меню. Каждые 12 px пути — три точки
  поперёк хода (на оси и ±5 px), elementFromPoint должен попасть в пустое место. Поле карточки (padding)
  попадает в саму карточку — это пусто. Свой дом (кнопка знака, активный пункт) — можно на первых и последних
  48 px пути. Запретные прямоугольники (док поиска, кнопка ИИ, тосты, всплывающие слои, кольцо фокуса) —
  расширенные на 4 px. Отказ безопасен: курьер берёт следующий якорь или отвечает «На месте».
*/

export const PROBE_STEP = 12;
export const PROBE_SPREAD = 5;
export const HOME_SKIP = 48;
/** Зонд места у посадки: шаг и глубина по нормали, px. */
const ROOM_STEP = 2;
const ROOM_REACH = 24;
/** Запретные прямоугольники расширяются на столько. */
const FORBID_PAD = 4;

/** Что занято: нажимается, вводится, рисует значок или ручку. Разделитель окон помечен как зазор дома. */
const BUSY = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="option"]',
  '[role="separator"]:not([data-shell-gap])',
  'svg',
  'img',
  'canvas',
].join(',');

/** Слои поверх, мимо которых капля не идёт. */
const FORBID = [
  '#search-dock',
  'button[aria-label="Открыть AI Spotlight"]',
  '[aria-live] > [role="status"]',
  '[data-radix-popper-content-wrapper]',
  '[role="tooltip"]',
].join(',');

/** Собственный текст элемента под точкой (с запасом 2 px): поля абзаца — пусто, буквы — заняты. */
function textAt(el: Element, x: number, y: number): boolean {
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType !== Node.TEXT_NODE || !n.textContent?.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(n);
    for (const r of Array.from(range.getClientRects())) {
      if (x >= r.left - 2 && x <= r.right + 2 && y >= r.top - 2 && y <= r.bottom + 2) return true;
    }
  }
  return false;
}

const within = (b: Box, x: number, y: number, pad = 0) =>
  x >= b.left - pad && x <= b.right + pad && y >= b.top - pad && y <= b.bottom + pad;

export interface Probe {
  /** Точка пуста; home — свой дом считается пустым; allow — цель тоже. */
  free(x: number, y: number, home?: boolean, allow?: Element | null): boolean;
  /** Весь путь; null — свободен, иначе причина. */
  route(pts: PathPoint[], homeAt: { start: boolean; end: boolean }): string | null;
  /** Место у посадки: 2·min(наружу, внутрь) − 4. */
  room(seat: SeatGeom, clip: Box, allow: Element | null): number;
  /** Посадка: круг тела + 4 в видимой части и пустые точки по нормали; null — годится. */
  landing(seat: SeatGeom, size: number, room: number, clip: Box, allow: Element | null): string | null;
}

export function makeProbe(read: PlanRead): Probe {
  const { ceiling } = read.plan;
  // Слой, что закрывается (data-state="closed" — доигрывает выход), уже не в счёт.
  const forbidden: Box[] = Array.from(document.querySelectorAll(FORBID))
    .filter(
      (el) => el.getClientRects().length > 0 && el.firstElementChild?.getAttribute('data-state') !== 'closed',
    )
    .map((el) => el.getBoundingClientRect());
  // Кольцо фокуса — снаружи элемента: elementFromPoint его не видит.
  const focus = document.activeElement;
  if (focus instanceof HTMLElement && focus !== document.body && focus.matches(':focus-visible')) {
    forbidden.push(focus.getBoundingClientRect());
  }
  const home = read.home;

  const free: Probe['free'] = (x, y, atHome = false, allow = null) => {
    if (y < ceiling || x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return false;
    for (const b of forbidden) if (within(b, x, y, FORBID_PAD)) return false;
    const el = document.elementFromPoint(x, y);
    if (!el) return false;
    if (atHome && home.some((h) => h.contains(el))) return true;
    if (allow && allow === el) return true;
    if (el.closest(BUSY)) return false;
    return !textAt(el, x, y);
  };

  const route: Probe['route'] = (pts, homeAt) => {
    let s = 0;
    let next = 0;
    const total = (() => {
      let L = 0;
      for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      return L;
    })();
    for (let i = 0; i < pts.length; i++) {
      if (i > 0) s += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      if (s < next && i < pts.length - 1) continue;
      next = s + PROBE_STEP;
      const p = pts[i];
      const atHome = (homeAt.start && s <= HOME_SKIP) || (homeAt.end && total - s <= HOME_SKIP);
      const nx = -Math.sin(p.h);
      const ny = Math.cos(p.h);
      for (const d of [0, -PROBE_SPREAD, PROBE_SPREAD]) {
        if (!free(p.x + nx * d, p.y + ny * d, atHome)) {
          return `занято на пути (${Math.round(p.x)}, ${Math.round(p.y)})`;
        }
      }
    }
    return null;
  };

  /** Точка у посадки: в видимой части и пустая (поле цели — пусто, её содержимое — занято). */
  const freeNear = (x: number, y: number, clip: Box, allow: Element | null) =>
    within(clip, x, y) && free(x, y, false, allow);

  const room: Probe['room'] = (seat, clip, allow) => {
    const [nx, ny] = seat.normal;
    const tx = -ny;
    const ty = nx;
    // По нормали наружу (1) и внутрь (−1) до первой занятой точки; поперёк — ±8 px (тело не тоньше).
    const reach = (dir: number) => {
      for (let d = ROOM_STEP; d <= ROOM_REACH; d += ROOM_STEP) {
        for (const lat of [0, -8, 8]) {
          if (!freeNear(seat.x + dir * nx * d + tx * lat, seat.y + dir * ny * d + ty * lat, clip, allow))
            return d;
        }
      }
      return ROOM_REACH + ROOM_STEP;
    };
    return 2 * Math.min(reach(1), reach(-1)) - 4;
  };

  const landing: Probe['landing'] = (seat, size, rm, clip, allow) => {
    const r = size / 2 + 2;
    if (
      seat.x - r < clip.left ||
      seat.x + r > clip.right ||
      seat.y - r < clip.top ||
      seat.y + r > clip.bottom
    ) {
      return 'посадка у края видимой части';
    }
    const [nx, ny] = seat.normal;
    for (const d of [0, rm / 2, -rm / 2]) {
      if (!freeNear(seat.x + nx * d, seat.y + ny * d, clip, allow)) return 'посадка занята';
    }
    return null;
  };

  return { free, route, room, landing };
}
